import React, { useEffect, useRef, useState } from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import Icon from "@mdi/react";
import { mdiCreation, mdiPencilOutline } from "@mdi/js";
import { request } from "utils/request";

const STATUS_PHRASES = [
  "Organizing details",
  "Processing visuals",
  "Compiling insights",
  "Checking observations",
];
const STATUS_INTERVAL_MS = 2200;
const STATUS_FADE_MS = 300;

// Visual states: empty / loading / view / editing.
const EditOverviewSummary = ({ reportId, initialSummary, onSaved, onToast }) => {
  const [mode, setMode] = useState(initialSummary ? "view" : "empty");
  const [summary, setSummary] = useState(initialSummary || "");
  const [editDraft, setEditDraft] = useState("");
  const [saving, setSaving] = useState(false);

  // Adopt parent updates to initialSummary unless the user is mid-edit.
  useEffect(() => {
    if (mode === "editing") return;
    setSummary(initialSummary || "");
    setMode(initialSummary ? "view" : "empty");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSummary]);

  const textareaRef = useRef(null);
  useEffect(() => {
    if (mode !== "editing") return;
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [mode, editDraft]);

  const [phraseIdx, setPhraseIdx] = useState(0);
  const [phraseShown, setPhraseShown] = useState(true);
  useEffect(() => {
    if (mode !== "loading") return undefined;
    const tick = setInterval(() => {
      setPhraseShown(false);
      setTimeout(() => {
        setPhraseIdx((current) => {
          const next = Math.floor(Math.random() * (STATUS_PHRASES.length - 1));
          return next >= current ? next + 1 : next;
        });
        setPhraseShown(true);
      }, STATUS_FADE_MS);
    }, STATUS_INTERVAL_MS);
    return () => clearInterval(tick);
  }, [mode]);

  const callSummaryGet = async () => {
    const data = await request(
      `${process.env.REACT_APP_API_URL}/reports/${reportId}/summary/`
    );
    return data?.summary || "";
  };

  const handleSynthesize = async () => {
    setMode("loading");
    try {
      const text = await callSummaryGet();
      setSummary(text);
      setMode(text ? "view" : "empty");
      onSaved?.(text);
    } catch (_err) {
      setMode(summary ? "view" : "empty");
      onToast?.({
        message: "Failed to generate summary. Please try again.",
        variant: "error",
      });
    }
  };

  const handleEdit = () => {
    setEditDraft(summary);
    setMode("editing");
  };

  const handleCancelEdit = () => {
    setEditDraft("");
    setMode("view");
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await request(
        `${process.env.REACT_APP_API_URL}/reports/${reportId}/`,
        {
          method: "PATCH",
          body: JSON.stringify({ summary: editDraft }),
        }
      );
      setSummary(editDraft);
      setMode("view");
      setEditDraft("");
      onSaved?.(editDraft);
      onToast?.({ message: "Summary saved.", variant: "success" });
    } catch (_err) {
      onToast?.({
        message: "Failed to save summary. Please try again.",
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  // Re-run AI on the current item set, discarding the local edit draft.
  const handleSynthesizeFromEdit = async () => {
    setEditDraft("");
    handleSynthesize();
  };

  return (
    <div className={`edit-overview-summary mode-${mode}`}>
      <div className="edit-overview-summary-header">
        <h2 className="edit-overview-title">Inspection Overview</h2>
        {mode === "view" && (
          <Button
            variant="outline-primary"
            size="sm"
            className="edit-overview-summary-edit-btn"
            onClick={handleEdit}
          >
            <Icon path={mdiPencilOutline} size={0.667} />
            Edit
          </Button>
        )}
        {mode === "editing" && (
          <Button
            variant="outline-primary"
            size="sm"
            className="edit-overview-synth-btn"
            onClick={handleSynthesizeFromEdit}
            disabled={saving}
          >
            <Icon path={mdiCreation} size={0.75} />
            Synthesize via AI
          </Button>
        )}
      </div>

      {mode === "empty" && (
        <div className="edit-overview-summary-empty">
          <p>Summarize all items below into an overview.</p>
          <Button
            variant="outline-primary"
            size="sm"
            className="edit-overview-synth-btn"
            onClick={handleSynthesize}
          >
            <Icon path={mdiCreation} size={0.75} />
            Synthesize via AI
          </Button>
        </div>
      )}

      {mode === "loading" && (
        <div className="edit-overview-summary-loading">
          <Spinner
            animation="border"
            variant="primary"
            className="edit-overview-summary-spinner"
          />
          <span
            className={`edit-overview-summary-phrase${
              phraseShown ? "" : " is-fading"
            }`}
          >
            {STATUS_PHRASES[phraseIdx]}...
          </span>
        </div>
      )}

      {mode === "view" && (
        <p className="edit-overview-summary-text">{summary}</p>
      )}

      {mode === "editing" && (
        <>
          <Form.Control
            as="textarea"
            ref={textareaRef}
            rows={1}
            value={editDraft}
            onChange={(e) => setEditDraft(e.target.value)}
            className="edit-overview-summary-textarea"
          />
          <div className="edit-overview-summary-editing-actions">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleCancelEdit}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSave}
              disabled={saving || !editDraft.trim()}
            >
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" /> Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default EditOverviewSummary;
